import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, 
  Save,
  Image as ImageIcon,
  Type,
  FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SiteSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState({
    // Homepage
    tagline: 'Creation. Momentum. Ownership.',
    tagline_highlight: 'Ownership.',
    featured_title: '',
    featured_image: '',
    in_motion_label: 'In Motion',
    
    // About page
    about_headline: '',
    about_intro: '',
    about_sections: [],
    
    // Footer
    footer_tagline: '',
    contact_email: 'dldagerman1@gmail.com',
    
    // SEO defaults
    default_seo_title: 'Cultural Revolution | Artist Ecosystem',
    default_seo_description: 'The central hub for an interconnected ecosystem of creator-led projects in film, media, and culture.'
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/site-settings`, {
          credentials: 'include'
        });
        
        if (response.status === 403) {
          navigate('/cms');
          return;
        }
        
        if (response.ok) {
          const data = await response.json();
          setSettings(prev => ({ ...prev, ...data }));
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/site-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        alert('Settings saved successfully!');
      }
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setSettings({ ...settings, [field]: `${API_URL}${result.url}` });
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="editorial-container py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/cms" className="text-xl editorial-serif">Cultural Revolution</Link>
            <span className="text-xs uppercase tracking-widest text-muted-foreground px-2 py-1 bg-muted">
              Site Settings
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/cms">
              <Button variant="ghost" size="sm">
                <ArrowLeft size={16} className="mr-2" />
                Back
              </Button>
            </Link>
            <Button onClick={handleSave} disabled={saving}>
              <Save size={16} className="mr-2" />
              {saving ? 'Saving...' : 'Save All'}
            </Button>
          </div>
        </div>
      </header>

      <div className="editorial-container py-8">
        <div className="max-w-3xl mx-auto space-y-8">
          
          {/* Homepage Settings */}
          <Card className="p-6">
            <h2 className="text-xl editorial-serif mb-4 flex items-center gap-2">
              <Type size={20} />
              Homepage Settings
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Tagline</label>
                <input
                  type="text"
                  value={settings.tagline}
                  onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  placeholder="Creation. Momentum. Ownership."
                />
                <p className="text-xs text-muted-foreground mt-1">Appears at top of homepage</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Highlight Word (shown in red)</label>
                <input
                  type="text"
                  value={settings.tagline_highlight}
                  onChange={(e) => setSettings({ ...settings, tagline_highlight: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  placeholder="Ownership."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">"In Motion" Section Label</label>
                <input
                  type="text"
                  value={settings.in_motion_label}
                  onChange={(e) => setSettings({ ...settings, in_motion_label: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                />
              </div>
            </div>
          </Card>

          {/* About Page Settings */}
          <Card className="p-6">
            <h2 className="text-xl editorial-serif mb-4 flex items-center gap-2">
              <FileText size={20} />
              About Page
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Headline</label>
                <input
                  type="text"
                  value={settings.about_headline}
                  onChange={(e) => setSettings({ ...settings, about_headline: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  placeholder="About Cultural Revolution"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Introduction Text</label>
                <textarea
                  value={settings.about_intro}
                  onChange={(e) => setSettings({ ...settings, about_intro: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  rows={4}
                  placeholder="Introduction paragraph for the About page..."
                />
              </div>
            </div>
          </Card>

          {/* Footer Settings */}
          <Card className="p-6">
            <h2 className="text-xl editorial-serif mb-4">Footer Settings</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Footer Tagline</label>
                <input
                  type="text"
                  value={settings.footer_tagline}
                  onChange={(e) => setSettings({ ...settings, footer_tagline: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  placeholder="Independent film & media ecosystem"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Contact Email</label>
                <input
                  type="email"
                  value={settings.contact_email}
                  onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                />
              </div>
            </div>
          </Card>

          {/* SEO Defaults */}
          <Card className="p-6">
            <h2 className="text-xl editorial-serif mb-4">Default SEO Settings</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Default Page Title</label>
                <input
                  type="text"
                  value={settings.default_seo_title}
                  onChange={(e) => setSettings({ ...settings, default_seo_title: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Default Meta Description</label>
                <textarea
                  value={settings.default_seo_description}
                  onChange={(e) => setSettings({ ...settings, default_seo_description: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  rows={2}
                />
              </div>
            </div>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default SiteSettingsPage;
